import styled from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import { AmountFormatted } from '@root/shared/core/format'
import { Checkbox, RadioButton } from '@root/shared/ui-kit'

import { WalletAddressTruncated } from '../common/wallet-address-truncated'

import type { WalletAddress } from '../../model'
import type { Size } from '@blade/ui-theme'
import type { ClassNameProps } from '@root/shared/react'
import type BigNumber from 'bignumber.js'

export type WalletSelectItemProps = {
  active?: boolean
  address: WalletAddress
  balanceNativeSlot: React.ReactNode
  balanceUsd: BigNumber
  onClick?: React.MouseEventHandler<HTMLElement>
  selectionMode?: 'check' | 'radio'
  size?: Extract<Size, 's' | 'm'>
  walletNameSlot: React.ReactNode
} & ClassNameProps

export const WalletSelectItem: React.FC<WalletSelectItemProps> = ({
  active = false,
  selectionMode = 'check',
  onClick,
  balanceNativeSlot,
  balanceUsd,
  address,
  size = 'm',
  className,
  walletNameSlot,
}) => {
  const haptics = useHapticFeedback()
  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!onClick) {
      return
    }

    haptics.impactOccurred('light')
    onClick(e)
  }

  return (
    <Root
      aria-selected={active}
      onClick={handleClick}
      data-size={size}
      className={className}
    >
      {selectionMode === 'check' ? (
        <Checkbox checked={active} />
      ) : (
        <RadioButton checked={active} />
      )}
      <WalletSeparator />
      <Column>
        <WalletAddressLine>
          {walletNameSlot}
          <Address
            walletAddress={address}
            withCopy
            copySize={size === 's' ? 11 : 14}
          />
        </WalletAddressLine>
      </Column>
      <Column>
        <WalletBalanceNative>{balanceNativeSlot}</WalletBalanceNative>
        <WalletBalanceUsd>
          <AmountFormatted
            amount={balanceUsd}
            roundingMode="trunc"
            currency="$"
          />
        </WalletBalanceUsd>
      </Column>
    </Root>
  )
}

const Root = styled.div`
  background: var(--background-1);

  border-radius: 6px;
  display: grid;
  grid-template-columns: auto 1px 1fr auto;
  gap: 8px;
  align-items: center;
  padding-inline: 8px 10px;

  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &[aria-selected='true'] {
    background: var(--appearanceA-7);
  }
  &:hover {
    background: var(--appearanceA-7);
  }
  &[data-size='s'] {
    height: 40px;
  }
  &[data-size='m'] {
    height: 48px;
  }
`

const Column = styled.div`
  display: grid;
  align-items: center;

  ${Root}[data-size='m'] & {
    padding-inline-start: 4px;
  }
`

const WalletAddressLine = styled.div`
  overflow: hidden;
  color: var(--text-8);
  text-overflow: ellipsis;
  white-space: nowrap;

  display: grid;
  gap: 2px;
`

const WalletSeparator = styled.div`
  width: 1px;
  height: 32px;
  border-radius: 2px;
  background: var(--backgroundA-3);
`

const WalletBalanceNative = styled.span`
  color: var(--text-12);
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;

  ${Root}[data-size='s'] & {
    font-size: 11px;
    font-weight: 600;
  }
  ${Root}[data-size='m'] & {
    font-size: 12px;
    font-weight: 700;
  }
`

const WalletBalanceUsd = styled.span`
  overflow: hidden;
  color: var(--text-9);
  text-align: right;
  text-overflow: ellipsis;

  ${Root}[data-size='s'] & {
    font-size: 10px;
    font-weight: 500;
  }
  ${Root}[data-size='m'] & {
    font-size: 12px;
    font-weight: 600;
  }
`

const Address = styled(WalletAddressTruncated)`
  color: var(--text-8);
  font-weight: 500;
  justify-content: start;
  gap: 2px;

  ${Root}[data-size='s'] & {
    font-size: 10px;
  }
  ${Root}[data-size='m'] & {
    font-size: 12px;
  }
`
